import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { GlassPanel } from "@/components/ui/glass-panel";
import { PageHeader } from "@/components/page-header";
import { cn } from "@/lib/utils";

interface Concept {
  id: "arc" | "ring" | "horizon";
  name: string;
  pitch: string;
  Mark: React.FC<{ size?: number; className?: string }>;
}

// ── Mark components ──────────────────────────────────────────────────────────

function MarkArc({ size = 32, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M4.5 9 L16 25 L27.5 9" />
      <path d="M10.5 8 L16 15 L21.5 8" opacity="0.55" />
      <path d="M14 7 L16 9.5 L18 7" opacity="0.32" />
    </svg>
  );
}

function MarkRing({ size = 32, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      className={className}
      aria-hidden
    >
      <path d="M3.5 22 A12.5 12.5 0 0 1 28.5 22" opacity="0.32" />
      <path d="M8 22 A8 8 0 0 1 24 22" opacity="0.6" />
      <path d="M12.2 22 A3.8 3.8 0 0 1 19.8 22" />
      <line x1="3" y1="22" x2="29" y2="22" strokeWidth={2.4} />
    </svg>
  );
}

function MarkHorizon({ size = 32, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      className={className}
      aria-hidden
    >
      <circle cx="16" cy="14" r="6.4" />
      <line x1="3.5" y1="20.5" x2="28.5" y2="20.5" strokeWidth={2.6} />
    </svg>
  );
}

const CONCEPTS: Concept[] = [
  {
    id: "arc",
    name: "Atmospheric arc",
    pitch: "Compressed atmospheric layers wedge to a focal point. Distinctive monogram, echoes the V from Vertali.",
    Mark: MarkArc,
  },
  {
    id: "ring",
    name: "Barometric ring",
    pitch: "Concentric arcs over a horizon. Reads as sunrise, isobars, or a radar sweep — instrument-y without being literal.",
    Mark: MarkRing,
  },
  {
    id: "horizon",
    name: "Horizon",
    pitch: "Sun/moon meets horizon. Calmest of the three, most weather-app native, simplest at small sizes.",
    Mark: MarkHorizon,
  },
];

// ── Lockup (mark + wordmark) ────────────────────────────────────────────────

function Lockup({
  Mark,
  variant = "light",
}: {
  Mark: Concept["Mark"];
  variant?: "light" | "dark";
}) {
  return (
    <div
      className={cn(
        "inline-flex items-center gap-2.5",
        variant === "light" ? "text-slate-900" : "text-white",
      )}
    >
      <Mark size={28} />
      <span className="text-xl font-black tracking-tight leading-none lowercase">
        vertali
        <span
          className={cn(
            "ml-1 font-medium",
            variant === "light" ? "text-slate-500" : "text-white/55",
          )}
        >
          weather
        </span>
      </span>
    </div>
  );
}

// ── Preview row per concept ──────────────────────────────────────────────────

function ConceptRow({ concept }: { concept: Concept }) {
  const { Mark, name, pitch, id } = concept;
  return (
    <GlassPanel intensity="low" className="p-5 sm:p-6">
      <div className="flex items-baseline justify-between gap-3 mb-5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-primary/85 font-bold">
            Concept {id === "arc" ? "1" : id === "ring" ? "2" : "3"}
          </p>
          <h2 className="text-base font-bold tracking-tight">{name}</h2>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed max-w-2xl">
            {pitch}
          </p>
        </div>
      </div>

      {/* Size sweep — 16, 24, 32, 48, 64, 96 */}
      <div className="rounded-xl border border-border/40 bg-background/30 p-4 mb-3">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-3">
          Mark · sized
        </p>
        <div className="flex items-end flex-wrap gap-5 text-foreground">
          {[16, 24, 32, 48, 64, 96].map((s) => (
            <div key={s} className="flex flex-col items-center gap-1.5">
              <div
                className="flex items-center justify-center"
                style={{ width: 96, height: 96 }}
              >
                <Mark size={s} />
              </div>
              <span className="text-[10px] tabular-nums text-muted-foreground">
                {s}px
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Lockups on different surfaces */}
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-xl border border-border/40 bg-white p-5">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-3">
            On light
          </p>
          <Lockup Mark={Mark} variant="light" />
        </div>
        <div className="rounded-xl border border-white/10 bg-slate-950 p-5">
          <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold mb-3">
            On dark
          </p>
          <Lockup Mark={Mark} variant="dark" />
        </div>
        <div
          className="rounded-xl border border-white/10 p-5"
          style={{
            background:
              "linear-gradient(135deg, oklch(0.07 0.05 270), oklch(0.18 0.1 250) 50%, oklch(0.07 0.05 270))",
          }}
        >
          <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold mb-3">
            On hero gradient
          </p>
          <Lockup Mark={Mark} variant="dark" />
        </div>
        <div
          className="rounded-xl border border-primary/30 p-5"
          style={{
            background:
              "color-mix(in oklab, var(--primary) 12%, var(--background))",
          }}
        >
          <p className="text-[10px] uppercase tracking-wider text-foreground/55 font-bold mb-3">
            On primary tint
          </p>
          <Lockup
            Mark={Mark}
            variant={
              // pick variant that contrasts best — primary tint is dark-ish on dark theme,
              // light on light theme. We'll just always use foreground via variant=dark
              // when in dark mode, light otherwise. Tailwind's dark: prefix handles it.
              "dark"
            }
          />
        </div>
      </div>

      {/* Blue brand variants */}
      <div className="mt-3">
        <p className="text-[10px] uppercase tracking-wider text-primary/85 font-bold mb-2">
          Blue brand variants
        </p>
        <div className="grid gap-3 md:grid-cols-3">
          {/* A: branded mark, white wordmark */}
          <div
            className="rounded-xl border border-white/10 p-5"
            style={{
              background:
                "linear-gradient(135deg, oklch(0.07 0.05 270), oklch(0.18 0.1 250) 50%, oklch(0.07 0.05 270))",
            }}
          >
            <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold mb-3">
              A · Branded mark
            </p>
            <span className="inline-flex items-center gap-2.5">
              <Mark size={28} className="text-primary drop-shadow-[0_0_18px_oklch(0.78_0.16_195/0.6)]" />
              <span className="text-xl font-black tracking-tight leading-none lowercase text-white">
                vertali
                <span className="ml-1 font-medium text-white/55">weather</span>
              </span>
            </span>
          </div>

          {/* B: branded mark + gradient wordmark */}
          <div
            className="rounded-xl border border-white/10 p-5"
            style={{
              background:
                "linear-gradient(135deg, oklch(0.07 0.05 270), oklch(0.18 0.1 250) 50%, oklch(0.07 0.05 270))",
            }}
          >
            <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold mb-3">
              B · Gradient wordmark
            </p>
            <span className="inline-flex items-center gap-2.5">
              <Mark size={28} className="text-primary drop-shadow-[0_0_18px_oklch(0.78_0.16_195/0.6)]" />
              <span className="text-xl font-black tracking-tight leading-none lowercase">
                <span className="bg-gradient-to-r from-sky-200 via-indigo-200 to-cyan-300 bg-clip-text text-transparent">
                  vertali
                </span>
                <span className="ml-1 font-medium text-white/55">weather</span>
              </span>
            </span>
          </div>

          {/* C: all-blue surface */}
          <div
            className="rounded-xl border border-white/10 p-5"
            style={{
              background:
                "linear-gradient(135deg, oklch(0.18 0.16 250) 0%, oklch(0.32 0.18 230) 50%, oklch(0.16 0.18 270) 100%)",
            }}
          >
            <p className="text-[10px] uppercase tracking-wider text-white/55 font-bold mb-3">
              C · All-blue surface
            </p>
            <span className="inline-flex items-center gap-2.5">
              <Mark size={28} className="text-white drop-shadow-[0_0_18px_oklch(0.95_0.05_220/0.6)]" />
              <span className="text-xl font-black tracking-tight leading-none lowercase text-white">
                vertali
                <span className="ml-1 font-medium text-white/65">weather</span>
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Sidebar simulation */}
      <div className="mt-3 rounded-xl border border-border/40 overflow-hidden">
        <div className="grid grid-cols-12">
          <div className="col-span-3 lg:col-span-2 bg-sidebar/70 backdrop-blur-xl p-4 flex items-center justify-start gap-2.5 border-r border-border/40 min-h-[64px]">
            <Mark size={28} className="text-primary" />
            <span className="text-sm font-black tracking-tight text-foreground">
              vertali
              <span className="ml-1 font-medium text-muted-foreground">weather</span>
            </span>
          </div>
          <div className="col-span-9 lg:col-span-10 p-4 bg-card/30">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
              Sidebar simulation
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              How the lockup looks in place of the current PNG.
            </p>
          </div>
        </div>
      </div>
    </GlassPanel>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function LogoPreviewPage() {
  return (
    <div className="space-y-6 animate-slide-up">
      <PageHeader
        eyebrow="Brand"
        title="Logo concepts"
        subtitle="Three directions — pick one and I'll wire it through favicon, sidebar, OG, and PWA icons."
        action={
          <Link
            to="/about"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
          >
            <ArrowLeft className="h-3 w-3" />
            Back
          </Link>
        }
      />

      {/* Side-by-side mark comparison */}
      <GlassPanel intensity="low" className="p-5 sm:p-6">
        <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold mb-4">
          Side-by-side · 64px
        </p>
        <div className="grid grid-cols-3 gap-3">
          {CONCEPTS.map((c) => (
            <div
              key={c.id}
              className="rounded-xl border border-border/40 bg-background/30 p-5 flex flex-col items-center"
            >
              <div className="flex h-20 w-20 items-center justify-center text-foreground">
                <c.Mark size={64} />
              </div>
              <p className="mt-3 text-xs font-bold text-center">{c.name}</p>
            </div>
          ))}
        </div>
      </GlassPanel>

      {CONCEPTS.map((c) => (
        <ConceptRow key={c.id} concept={c} />
      ))}

      <p className="text-center text-[11px] text-muted-foreground py-4">
        Tell me which one to develop and I'll generate the final SVG set.
      </p>
    </div>
  );
}
